import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Power } from 'lucide-react';
import { PE_ONE_LOGO_SRC } from '../constants/brandAssets';

// --- KONFIGURATION ---
const TIMING = {
    PHASE_1_BIOS: 3500,     
    PHASE_2_LOAD: 5500,     
    PHASE_3_SECURE: 2000,   
    PHASE_4_LOGO: 4000,     // 4 Sekunden Logo-Show (Genug Zeit für den Boom)
};

// V1.1 FIX: Alle Systeme melden jetzt sauber [OK]
const bootLines = [
    "> INITIALIZING PREIS.EXPERT BIOS v4.0... [OK]",
    "> CHECKING MEMORY INTEGRITY... [OK]",
    "> MOUNTING VIRTUAL FILE SYSTEM... [OK]",
    "> LOAD DRIVER: CPU_GOVERNOR... [OK]",
    "> LOAD DRIVER: CRYPTO_ENGINE... [OK]",
    "> BYPASSING SECURITY PROTOCOLS... [OK]",
    "> ESTABLISHING SECURE CONNECTION... [OK]",
    "> USER AUTHENTICATION: VERIFIED... [OK]",
    "> DECRYPTING LOCAL STORAGE... [OK]",
    "> STARTING CORE ENGINE... [OK]"
];

const SystemBootSequence: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
    // 💉 KORREKTUR: Anti-Nerv-Protokoll (God-Mode Bypass & Refresh Bypass)
    const isGodMode = !!sessionStorage.getItem('GOD_MODE_ID');
    const isAlreadyBooted = !!sessionStorage.getItem('SYSTEM_BOOTED');
    
    const [hasInteracted, setHasInteracted] = useState(false);
    const [phase, setPhase] = useState<1 | 2 | 3 | 4>(1);
    const [textLines, setTextLines] = useState<string[]>([]);
    const [progress, setProgress] = useState(0);
    
    // RADAR CURSOR STATE
    const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
    const [isMouseVisible, setIsMouseVisible] = useState(false);

    // Audio Refs
    const audioSequenceRef = useRef<HTMLAudioElement | null>(null);
    const audioImpactRef = useRef<HTMLAudioElement | null>(null);

    // --- BYPASS LOGIK (Überspringt bei F5 oder Admin God-Mode) ---
    useEffect(() => {
        if (isGodMode || isAlreadyBooted) {
            onComplete();
        }
    }, [isGodMode, isAlreadyBooted, onComplete]);

    // --- MOUSE TRACKER ---
    useEffect(() => {
        if (isGodMode || isAlreadyBooted) return;
        const handleMouseMove = (e: MouseEvent) => {
            setMousePos({ x: e.clientX, y: e.clientY });
            if (!isMouseVisible) setIsMouseVisible(true);
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [isMouseVisible, isGodMode, isAlreadyBooted]);

    // --- AUDIO FADER ---
    const fadeOutAudio = (duration: number) => {
        const steps = 20; 
        const stepTime = duration / steps; 
        
        const fadeInterval = setInterval(() => {
            if (audioSequenceRef.current && audioSequenceRef.current.volume > 0.05) {
                audioSequenceRef.current.volume = Math.max(0, audioSequenceRef.current.volume - 0.05);
            }
            if (audioImpactRef.current && audioImpactRef.current.volume > 0.05) {
                audioImpactRef.current.volume = Math.max(0, audioImpactRef.current.volume - 0.05);
            }

            if (audioSequenceRef.current?.volume && audioSequenceRef.current.volume <= 0.05 && 
                audioImpactRef.current?.volume && audioImpactRef.current.volume <= 0.05) {
                
                if (audioSequenceRef.current) audioSequenceRef.current.pause();
                if (audioImpactRef.current) audioImpactRef.current.pause();
                clearInterval(fadeInterval);
            }
        }, stepTime);
    };

    // --- BUTTON CLICK HANDLER ---
    const startBootSequence = () => {
        setHasInteracted(true);

        audioSequenceRef.current = new Audio('/boot_sequence.mp3');
        audioSequenceRef.current.volume = 0.6;
        audioSequenceRef.current.play().catch(e => console.error("Audio 1 failed:", e));

        audioImpactRef.current = new Audio('/boot_impact.mp3');
        audioImpactRef.current.volume = 1.0;
        audioImpactRef.current.load();
    };

    // --- PHASE 1: TEXT TERMINAL ---
    useEffect(() => {
        if (!hasInteracted || isGodMode || isAlreadyBooted) return;

        let lineIndex = 0;
        const interval = setInterval(() => {
            if (lineIndex < bootLines.length) {
                setTextLines(prev => {
                    const newLines = [...prev, bootLines[lineIndex]];
                    if (newLines.length > 8) return newLines.slice(newLines.length - 8);
                    return newLines;
                });
                lineIndex++;
            }
        }, 350); 

        const timer = setTimeout(() => {
            clearInterval(interval);
            setPhase(2);
        }, TIMING.PHASE_1_BIOS);

        return () => { clearInterval(interval); clearTimeout(timer); };
    }, [hasInteracted, isGodMode, isAlreadyBooted]);

    // --- PHASE 2: LOADING BAR ---
    useEffect(() => {
        if (!hasInteracted || phase !== 2 || isGodMode || isAlreadyBooted) return;

        const steps = [5, 15, 25, 40, 45, 60, 75, 85, 90, 95];
        let stepIdx = 0;
        const stepTime = TIMING.PHASE_2_LOAD / steps.length;
        
        const interval = setInterval(() => {
            if (stepIdx < steps.length) {
                setProgress(steps[stepIdx]);
                stepIdx++;
            }
        }, stepTime);

        const timer = setTimeout(() => {
            setPhase(3);
        }, TIMING.PHASE_2_LOAD);

        return () => { clearInterval(interval); clearTimeout(timer); };
    }, [phase, hasInteracted, isGodMode, isAlreadyBooted]);

    // --- PHASE 3: SECURITY ---
    useEffect(() => {
        if (!hasInteracted || phase !== 3 || isGodMode || isAlreadyBooted) return;
        
        setProgress(100);
        const timer = setTimeout(() => {
            setPhase(4);
        }, TIMING.PHASE_3_SECURE);
        return () => clearTimeout(timer);
    }, [phase, hasInteracted, isGodMode, isAlreadyBooted]);

    // --- PHASE 4: LOGO & AUDIO CONTROL ---
    useEffect(() => {
        if (!hasInteracted || phase !== 4 || isGodMode || isAlreadyBooted) return;

        if (audioImpactRef.current) {
            audioImpactRef.current.currentTime = 0;
            audioImpactRef.current.play().catch(e => console.log("Impact Audio failed:", e));
        }

        const fadeOutStart = TIMING.PHASE_4_LOGO - 2000; 
        
        const fadeTimer = setTimeout(() => {
            fadeOutAudio(1500); 
        }, fadeOutStart);

        const completeTimer = setTimeout(() => {
            // 💉 Merken, dass die Bootsequenz fertig ist
            sessionStorage.setItem('SYSTEM_BOOTED', 'true');
            onComplete();
        }, TIMING.PHASE_4_LOGO);

        return () => { clearTimeout(fadeTimer); clearTimeout(completeTimer); };
    }, [phase, hasInteracted, onComplete, isGodMode, isAlreadyBooted]);

    // Falls Admin oder schon gebootet, rendere NICHTS vom Overlay.
    if (isGodMode || isAlreadyBooted) return null;

    return (
        <motion.div
            className="fixed inset-0 z-[9999] bg-black overflow-hidden cursor-none flex items-center justify-center font-mono"
            initial={{ opacity: 1 }}
            exit={{ 
                opacity: 0, 
                scale: 1.1, 
                filter: "blur(20px)",
                transition: { duration: 1.2, ease: "easeInOut" } 
            }}
        >
            {isMouseVisible && (
                <>
                    <motion.div 
                        className="pointer-events-none fixed top-0 left-0 z-[10000] w-2 h-2 bg-[#E6C299] rounded-full shadow-[0_0_10px_#E6C299]"
                        animate={{ x: mousePos.x - 4, y: mousePos.y - 4 }}
                        transition={{ type: "spring", stiffness: 1000, damping: 40 }}
                    />
                    <motion.div 
                        className="pointer-events-none fixed top-0 left-0 z-[9999] w-8 h-8 border border-[#E6C299]/50 rounded-full"
                        animate={{ x: mousePos.x - 16, y: mousePos.y - 16 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    />
                </>
            )}

            <div className="absolute inset-0 opacity-20 pointer-events-none" 
                style={{ 
                    backgroundImage: `linear-gradient(rgba(230, 194, 153, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(230, 194, 153, 0.05) 1px, transparent 1px)`, 
                    backgroundSize: '40px 40px',
                    maskImage: 'radial-gradient(circle at center, black 30%, transparent 90%)'
                }} 
            />

            <AnimatePresence mode="wait">
                {!hasInteracted ? (
                    <motion.div
                        key="preboot"
                        className="z-50 text-center cursor-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.3 } }}
                        onClick={startBootSequence}
                    >
                         <motion.div 
                            className="w-24 h-24 mx-auto border-2 border-[#E6C299]/30 rounded-full flex items-center justify-center mb-8 hover:border-[#E6C299] hover:bg-[#E6C299]/10 hover:shadow-[0_0_40px_rgba(230,194,153,0.4)] transition-all duration-300"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Power className="w-10 h-10 text-[#E6C299]" />
                        </motion.div>
                        <h2 className="text-[#E6C299] tracking-[0.4em] text-xs font-bold animate-pulse">SYSTEM INITIALISIEREN</h2>
                    </motion.div>
                ) : (
                    <motion.div
                        key="sequence"
                        className="relative z-10 w-full h-full flex flex-col items-center justify-center p-8 text-[#E6C299]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        {phase === 1 && (
                            <div className="absolute bottom-10 left-10 text-sm md:text-base space-y-1 opacity-70 font-bold tracking-wide">
                                {textLines.map((line, i) => ( <div key={i}>{line}</div> ))}
                                <div className="animate-pulse text-[#E6C299]">_</div>
                            </div>
                        )}

                        {(phase === 2 || phase === 3) && (
                            <motion.div 
                                className="w-full max-w-3xl flex flex-col items-center"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                            >
                                <div className="w-full flex justify-between text-xs uppercase tracking-[0.3em] mb-3 opacity-80 text-[#E6C299]">
                                    <span>{phase === 2 ? "Initializing Core" : "Verifying User Token"}</span>
                                    <span>{progress}%</span>
                                </div>
                                <div className="h-2 w-full bg-[#E6C299]/5 border border-[#E6C299]/20 relative overflow-hidden rounded-sm">
                                    <motion.div 
                                        className={`h-full ${phase === 3 ? 'bg-white shadow-[0_0_30px_white]' : 'bg-[#E6C299] shadow-[0_0_20px_#E6C299]'}`}
                                        initial={{ width: "0%" }}
                                        animate={{ width: `${progress}%` }}
                                        transition={{ ease: "circOut", duration: 0.5 }}
                                    />
                                </div>
                                {phase === 3 && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-6 text-sm tracking-[0.5em] text-white animate-pulse"
                                    >
                                        ACCESS GRANTED
                                    </motion.div>
                                )}
                            </motion.div>
                        )}

                        {phase === 4 && (
                            <motion.div 
                                className="flex flex-col items-center"
                                initial={{ opacity: 0, scale: 0.8, filter: "blur(20px)" }}
                                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                                transition={{ duration: 0.3, ease: "easeOut" }} 
                            >
                                <div className="relative group">
                                    <div className="absolute -inset-10 bg-[#E6C299] rounded-full opacity-20 blur-3xl animate-pulse"></div>
                                    <img 
                                        src={PE_ONE_LOGO_SRC}
                                        alt="PREIS.EXPERT Logo" 
                                        className="w-64 md:w-96 h-auto object-contain relative z-10 drop-shadow-[0_0_25px_rgba(230,194,153,0.5)]"
                                    />
                                </div>
                                <motion.div 
                                    className="mt-12 text-sm md:text-xl tracking-[0.8em] text-[#E6C299] font-black border-t border-[#E6C299]/30 pt-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5, duration: 1 }}
                                >
                                    SYSTEM READY
                                </motion.div>
                            </motion.div>
                        )}

                        <div className="absolute bottom-0 left-0 h-0.5 bg-[#E6C299]" 
                             style={{ 
                                 width: phase === 1 ? '25%' : (phase === 2 ? '60%' : (phase === 3 ? '85%' : '100%')), 
                                 transition: 'width 4s cubic-bezier(0.4, 0, 0.2, 1)', opacity: 0.5
                             }} 
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default SystemBootSequence;